import { AlertCircle, FileDown, FileText, SaudiRiyal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { GetSecondOpinionById } from '@virtual-care/client';
import {
  formatSecondOpinionDueDate,
  getLocalizedSpeciality,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
} from '@virtual-care/shadcn';
import { ReservationStatus } from '@virtual-care/ui';

import { useLanguage } from '../../../i18n/use-language.ts';
import type { StatusMap } from './status.ts';

interface AppointmentCardProps {
  record: GetSecondOpinionById;
  statusInfoMap: StatusMap;
  patientSummary: Array<{ label: string; value: string }>;
  onViewAttachments: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  isDoctor?: boolean;
  totalAmount?: string;
}

export function SecondOpinionCard({
  record,
  statusInfoMap,
  patientSummary,
  onViewAttachments,
  children,
  footer,
  totalAmount,
}: AppointmentCardProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const speciality = getLocalizedSpeciality(record.speciality, language);
  const doctorName = record?.doctor
    ? `${record.doctor.firstName} ${record.doctor.lastName}`
    : t('common.notSpecified');

  return (
    <Card className="relative overflow-hidden rounded-2xl pb-0">
      <CardHeader className="flex flex-row items-start justify-between px-6">
        <div className="flex items-center gap-1.5">
          <AlertCircle className="h-6 w-6 text-gray-600" />
          <CardTitle className="font-bold md:text-xl">
            {t('secondOpinion.card.titleWithSpecialty', {
              specialty: speciality.name,
            })}
          </CardTitle>
        </div>
        <div className="absolute inset-e-4 top-6 hidden md:block">
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground font-bold">
                {t('secondOpinion.card.statusLabel')}
              </p>
              <ReservationStatus.Root
                status={record.status}
                className="rounded-4xl px-3"
              >
                <ReservationStatus.Indicator className="h-2 w-2" />
                <ReservationStatus.Label render={(meta) => t(meta.key)} />
              </ReservationStatus.Root>
            </div>
            <p>
              <span className="text-primary flex items-center gap-x-1">
                <span className="text-muted-foreground font-bold">
                  {t('secondOpinion.card.priceLabel')}
                </span>
                {totalAmount} <SaudiRiyal className="h-4 w-4" />
              </span>
            </p>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
              <span className="text-xs text-blue-500">
                {t('secondOpinion.card.paid')}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 px-6">
        <div className="space-y-2 text-sm">
          <p>
            <span className="font-semibold text-gray-500">
              {t('secondOpinion.card.requestNumber')}{' '}
            </span>
            #{record.counter}
          </p>

          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-500">
              {t('secondOpinion.card.priority')}
            </span>
            <Badge
              className={cn(
                'py-1',
                record.urgent ? 'bg-red-500' : 'bg-blue-500',
              )}
            >
              {record.urgent ? t('common.urgent') : t('common.normal')}
            </Badge>
          </div>

          <p>
            <span className="font-semibold text-gray-500">
              {t('secondOpinion.card.dueDate')}
            </span>{' '}
            {formatSecondOpinionDueDate(record)}
          </p>

          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-500">
              {t('secondOpinion.card.serviceType')}
            </span>
            <Badge className={cn('bg-chart-2 py-1')}>
              {t('common.services.secondOpinion')}
            </Badge>
          </div>

          <div className="flex flex-col gap-1 md:hidden">
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground font-bold">
                {t('secondOpinion.card.statusLabel')}
              </p>
              <ReservationStatus.Root
                status={record.status}
                className="rounded-md"
              >
                <ReservationStatus.Label render={(meta) => t(meta.key)} />
              </ReservationStatus.Root>
            </div>
            <p>
              <span className="text-muted-foreground font-bold">
                {t('secondOpinion.card.priceLabel')}{' '}
              </span>
              -
            </p>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
              <span className="text-xs text-blue-500">
                {t('secondOpinion.card.paid')}
              </span>
            </div>
          </div>
          <p>
            <span className="font-semibold text-gray-500">
              {t('secondOpinion.card.caseDoctor')}{' '}
            </span>
            {doctorName}
          </p>
        </div>

        {statusInfoMap[record.status] && (
          <div
            className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${statusInfoMap[record.status].className}`}
          >
            {(() => {
              const Icon = statusInfoMap[record.status].icon;
              return <Icon className="h-4 w-4" />;
            })()}
            {statusInfoMap[record.status].text}
          </div>
        )}

        <div className="mt-6 flex items-center gap-1.5">
          <FileText className="h-6 w-6 text-gray-600" />
          <CardTitle className="text-lg font-black">
            {t('secondOpinion.card.attachmentsHeader')}
          </CardTitle>
        </div>

        <div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg px-4">
            {patientSummary.map((item) => (
              <div key={item.label} className="flex items-baseline gap-2">
                <p className="text-foreground text-base font-bold">
                  {item.label}:
                </p>
                <p className="bg-muted text-muted-foreground rounded-lg px-3 py-1">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          {record.files.length > 0 && (
            <Button
              variant="outline"
              className="float-end mt-4 gap-2"
              onClick={onViewAttachments}
            >
              <FileDown className="h-4 w-4" />
              {t('secondOpinion.card.viewAttachments')}
            </Button>
          )}
        </div>

        {children}
      </CardContent>

      <CardFooter className="bg-muted flex items-center justify-end gap-x-2 px-6 py-3.5">
        <div />
        {footer}
      </CardFooter>
    </Card>
  );
}
